import { 
  IndianRupee, 
  TrendingUp, 
  LineChart, 
  Shield, 
  Zap,
  Sparkles,
  Users,
  Camera
} from "lucide-react";

const features = [
  {
    icon: IndianRupee,
    title: "Cash Clarity",
    description: "Know exactly where your cash is, every single day. No surprises, no confusion.",
    gradient: "from-primary to-primary-dark",
    size: "large",
  },
  {
    icon: TrendingUp,
    title: "Profit Tracking",
    description: "See your real profit daily. Make smarter decisions.",
    gradient: "from-success to-primary",
    size: "small",
  },
  {
    icon: LineChart,
    title: "Loan Ready",
    description: "Bank-ready reports, instantly generated.",
    gradient: "from-accent to-accent-glow",
    size: "small",
  },
  {
    icon: Camera,
    title: "AI Receipt Scan",
    description: "Upload a photo, let AI fill the numbers. No more manual data entry.",
    gradient: "from-primary to-accent",
    size: "large",
  },
  {
    icon: Users,
    title: "Team Access",
    description: "Add staff with role-based permissions.",
    gradient: "from-primary-dark to-primary",
    size: "small",
  },
  {
    icon: Shield,
    title: "Secure Data",
    description: "Bank-grade encryption for your business.",
    gradient: "from-success to-primary-dark",
    size: "small",
  },
  {
    icon: Sparkles,
    title: "AI Copilot",
    description: "Ask questions about your business in plain language and get instant insights.",
    gradient: "from-accent to-primary",
    size: "large",
  },
  {
    icon: Zap,
    title: "Lightning Fast",
    description: "Close in 60 seconds, built for busy owners.",
    gradient: "from-accent-glow to-accent",
    size: "small",
  },
];

export const FeaturesSection = () => {
  return (
    <section id="features" className="relative py-20 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/30 to-background" />
      <div className="absolute top-1/4 left-0 w-1/2 h-96 bg-primary/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 right-0 w-1/2 h-96 bg-accent/5 rounded-full blur-[120px]" />

      <div className="container relative z-10 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <div className="text-center space-y-4 mb-12 md:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Powerful Features</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-balance px-2">
              Everything You Need to
              <br className="hidden sm:block" />
              <span className="text-gradient"> Run Your Business</span>
            </h2>
            <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
              Simple, powerful tools designed specifically for Indian small businesses
            </p>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`group relative p-6 md:p-8 rounded-2xl md:rounded-3xl bg-card border border-border hover:border-primary/50 transition-all duration-500 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1
                  ${feature.size === 'large' ? 'sm:col-span-2' : ''}`}
              >
                {/* Hover gradient */}
                <div className={`absolute inset-0 rounded-2xl md:rounded-3xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />
                
                {/* Icon */}
                <div className={`relative w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 md:mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="h-6 w-6 md:h-7 md:w-7 text-primary-foreground" />
                </div>

                {/* Content */}
                <h3 className="font-display text-lg md:text-xl font-semibold mb-2 md:mb-3 group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};